import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];
const IMG = "https://pub-a4d6ad10dc1a4e1da401961c64a9749e.r2.dev/projects/9de52582-87d6-4c3d-abe1-f1f808ef322a/img";

const photos = [
  { src: `${IMG}/35e69571c50df999.png`, cat: "operazioni", c: false },
  { src: `${IMG}/4babffd3bd351e31.jpg`, cat: "urbano", c: true },
  { src: `${IMG}/4f06aebb70a6a8c2.jpg`, cat: "equipaggiamento", c: false },
  { src: `${IMG}/6af9590ced611ade.jpg`, cat: "equipaggiamento", c: true },
  { src: `${IMG}/762b74d21df20437.jpg`, cat: "operazioni", c: false },
  { src: `${IMG}/acdafecf7e4f562d.jpg`, cat: "urbano", c: true },
  { src: `${IMG}/dcf8394b212aa4f0.png`, cat: "operazioni", c: false },
  { src: `${IMG}/ea6e66e9d010853f.jpg`, cat: "equipaggiamento", c: false },
  { src: `${IMG}/eac6d6522648daf2.jpg`, cat: "squadra", c: false },
  { src: `${IMG}/f24ee65bec868fd4.jpg`, cat: "urbano", c: true },
  { src: `${IMG}/fb3b583a35898baa.jpg`, cat: "squadra", c: false },
];

const SpotlightCard = ({ photo, onOpen, index }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  return (
    <motion.button
      ref={ref}
      data-testid={`gallery-card-${index}`}
      onMouseMove={onMove}
      onClick={() => onOpen(photo)}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: EASE, delay: (index % 3) * 0.12 }}
      className="chamfer group relative block w-full text-left border border-[#28342C] bg-[#111613] overflow-hidden hover:border-[#5B7052] transition-colors duration-300"
    >
      <div
        className="absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(320px circle at ${pos.x}% ${pos.y}%, rgba(200,180,141,0.14), transparent 65%)` }}
      />
      <div className="overflow-hidden">
        <img
          src={photo.src}
          alt={photo.label}
          loading="lazy"
          className="w-full h-64 object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
        />
      </div>
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <div className="font-heading text-sm font-bold uppercase tracking-wider text-[#E6ECE8]">{photo.label}</div>
          <div className="font-mono2 text-[10px] tracking-[0.25em] text-[#5C6A60] mt-0.5">{photo.loc}</div>
        </div>
        <Maximize2 className="w-4 h-4 text-[#5C6A60] group-hover:text-[#C8B48D] transition-colors duration-300" />
      </div>
    </motion.button>
  );
};

export const Gallery = () => {
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(null);
  const { t } = useLang();

  const merged = photos.map((p, i) => ({
    ...p,
    label: t.gallery.photos[i].label,
    loc: t.gallery.photos[i].loc,
    status: p.c ? t.gallery.classified : t.gallery.declassified,
  }));
  const visible = filter === "tutti" ? merged : merged.filter((p) => p.cat === filter);

  return (
    <section id="galleria" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 bg-[#0D110E]" data-testid="gallery-section">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="font-mono2 text-xs tracking-[0.35em] text-[#C8B48D] mb-4"
      >
        {t.gallery.eyebrow}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-10"
      >
        {t.gallery.titleA} <span className="text-stroke">{t.gallery.titleB}</span>
      </motion.h2>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
        {t.gallery.filters.map((f) => (
          <button
            key={f.id}
            data-testid={`gallery-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`chamfer-sm font-mono2 text-[11px] tracking-widest uppercase px-4 py-2 border transition-all duration-300 ${
              filter === f.id
                ? "border-[#C8B48D] text-[#0A0D0B] bg-[#C8B48D]"
                : "border-[#28342C] text-[#9EACA2] hover:border-[#5B7052] hover:text-[#E6ECE8]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {visible.map((p, i) => (
            <SpotlightCard key={p.src} photo={p} index={i} onOpen={setOpen} />
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] bg-[#0A0D0B]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 16 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="chamfer max-w-4xl w-full border border-[#5B7052] bg-[#111613] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={open.src} alt={open.label} className="w-full max-h-[65vh] object-cover" />
              <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-t border-[#28342C]">
                <div>
                  <div className="font-heading text-lg font-bold uppercase tracking-wider text-[#E6ECE8]">{open.label}</div>
                  <div className="font-mono2 text-[10px] tracking-[0.25em] text-[#5C6A60] mt-1">
                    LOC: {open.loc} — STATUS: <span className={open.c ? "text-[#EF4444]" : "text-[#22C55E]"}>{open.status}</span>
                  </div>
                </div>
                <button
                  data-testid="lightbox-close-button"
                  onClick={() => setOpen(null)}
                  className="chamfer-sm border border-[#5B7052] p-3 text-[#9EACA2] hover:text-[#C8B48D] hover:border-[#C8B48D] transition-colors duration-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
